import { supabase } from '@/lib/supabase';

export interface WLLSHonestBacktestConfig {
  fileId: string;
  startPosition: number;
  endPosition?: number;
  patternRefreshFrequency: number;
  winThreshold: number;
  directionThreshold?: number;
  minSequenceLength?: number;
  maxSequenceLength?: number;
  backtestMode: 'binary' | 'side_aware';
}

export interface WLLSHonestBacktestPosition {
  position: number;
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  actualResult: string; // WLLS tokens like '1L', '0S'
  confidence: number;
  currentPatternCount: number;
  usedPatterns: number;
  newPatternsAdded: number;
  patternBreakdown?: {
    patternsUsed: Array<{
      pattern: string;
      frequency: number;
      winRate: number;
      longRate: number;
      confidenceScore: number;
    }>;
  };
  skipReason?: string;
  winProbability?: number;
  lossProbability?: number;
  longProbability?: number;
  shortProbability?: number;
}

export interface WLLSHonestBacktestResult {
  config: WLLSHonestBacktestConfig;
  positions: WLLSHonestBacktestPosition[];
  summary: {
    totalPositions: number;
    predictedPositions: number;
    skippedPositions: number;
    correctPredictions: number;
    accuracy: number;
    directionalAccuracy: number; // WLLS enhancement
    skipRate: number;
    finalPatternCount: number;
    initialPatternCount: number;
    averagePatternsPerPosition: number;
    maxPatterns: number;
    minPatterns: number;
  };
  patternEvolution: Array<{
    position: number;
    patternCount: number;
    newPatterns: number;
    updatedPatterns: number;
  }>;
  processingTime: number;
  simulationId: string;
}

export interface StoredWLLSHonestBacktestSimulation {
  id: string;
  file_id: string;
  user_id: string;
  backtest_mode: 'binary' | 'side_aware';
  start_position: number;
  end_position?: number;
  pattern_refresh_frequency: number;
  win_threshold: number;
  direction_threshold?: number;
  min_sequence_length: number;
  max_sequence_length: number;
  total_positions: number;
  predicted_positions: number;
  skipped_positions: number;
  correct_predictions: number;
  accuracy: number;
  skip_rate: number;
  positions_data: WLLSHonestBacktestPosition[];
  pattern_evolution: Array<{
    position: number;
    patternCount: number;
    newPatterns: number;
    updatedPatterns: number;
  }>;
  processing_time_ms: number;
  summary_stats: {
    finalPatternCount: number;
    initialPatternCount: number;
    averagePatternsPerPosition: number;
    maxPatterns: number;
    minPatterns: number;
    directionalAccuracy: number;
  };
  created_at: string;
  updated_at: string;
}

export class WLLSHonestBacktestService {
  static async runBacktest(
    config: WLLSHonestBacktestConfig,
    onProgress?: (progress: { current: number; total: number; status: string }) => void
  ): Promise<{ success: boolean; result?: WLLSHonestBacktestResult; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('wlls-honest-progressive-backtest', {
        body: { config }
      });

      if (error) {
        console.error('Edge function error:', error);
        return { success: false, error: error.message };
      }

      if (!data?.result) {
        return { success: false, error: 'No result returned from WLLS backtest' };
      }

      return { success: true, result: data.result };
    } catch (error) {
      console.error('Error running WLLS honest backtest:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async getSimulations(fileId: string): Promise<{ 
    success: boolean; 
    data?: StoredWLLSHonestBacktestSimulation[]; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .from('wlls_honest_backtest_simulations')
        .select('*')
        .eq('file_id', fileId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching WLLS honest backtest simulations:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async getSimulationDetail(simulationId: string): Promise<{ 
    success: boolean; 
    data?: StoredWLLSHonestBacktestSimulation; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .from('wlls_honest_backtest_simulations')
        .select('*')
        .eq('id', simulationId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching WLLS honest backtest simulation detail:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async deleteSimulation(simulationId: string): Promise<{ 
    success: boolean; 
    error?: string 
  }> {
    try {
      // Delete patterns first (foreign key constraint)
      await supabase
        .from('wlls_honest_backtest_patterns')
        .delete()
        .eq('simulation_id', simulationId);

      // Then delete simulation
      const { error } = await supabase
        .from('wlls_honest_backtest_simulations')
        .delete()
        .eq('id', simulationId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting WLLS honest backtest simulation:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Get patterns from a specific simulation for comparison
  static async getSimulationPatterns(simulationId: string): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('wlls_honest_backtest_patterns')
        .select('*')
        .eq('simulation_id', simulationId)
        .order('confidence_score', { ascending: false });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('Error fetching WLLS simulation patterns:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to fetch WLLS simulation patterns' 
      };
    }
  }

  // Compare patterns between honest backtest and real-time prediction
  static async comparePatterns(simulationId: string, fileId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Get honest backtest patterns
      const honestPatterns = await this.getSimulationPatterns(simulationId);
      if (!honestPatterns.success) {
        return honestPatterns;
      }

      // Get real-time prediction patterns  
      const { data: realtimePatterns, error: realtimeError } = await supabase
        .from('wlls_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('confidence_score', { ascending: false });

      if (realtimeError) {
        throw realtimeError;
      }

      // Create comparison analysis
      const comparison = {
        honest_backtest: {
          total_patterns: honestPatterns.data?.length || 0,
          patterns: honestPatterns.data || []
        },
        realtime_prediction: {
          total_patterns: realtimePatterns?.length || 0,
          patterns: realtimePatterns || []
        },
        differences: this.analyzeWLLSPatternDifferences(honestPatterns.data || [], realtimePatterns || [])
      };

      return { success: true, data: comparison };
    } catch (error: any) {
      console.error('Error comparing WLLS patterns:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to compare WLLS patterns' 
      };
    }
  }

  // Analyze differences between honest backtest and realtime WLLS patterns
  private static analyzeWLLSPatternDifferences(honestPatterns: any[], realtimePatterns: any[]) {
    const honestMap = new Map(honestPatterns.map(p => [p.pattern, p]));
    const realtimeMap = new Map(realtimePatterns.map(p => [p.pattern, p]));
    
    const analysis = {
      common_patterns: [] as any[],
      honest_only: [] as any[],
      realtime_only: [] as any[],
      win_rate_differences: [] as any[],
      directional_differences: [] as any[] // WLLS enhancement
    };

    // Find common patterns and differences
    for (const [pattern, honestData] of honestMap) {
      if (realtimeMap.has(pattern)) {
        const realtimeData = realtimeMap.get(pattern);
        analysis.common_patterns.push({
          pattern,
          honest: honestData,
          realtime: realtimeData,
          win_rate_diff: Math.abs(honestData.win_rate - realtimeData.win_rate),
          long_rate_diff: Math.abs((honestData.long_rate || 0) - (realtimeData.long_rate || 0))
        });
        
        // Track win rate differences
        if (Math.abs(honestData.win_rate - realtimeData.win_rate) > 10) {
          analysis.win_rate_differences.push({
            pattern,
            honest_win_rate: honestData.win_rate,
            realtime_win_rate: realtimeData.win_rate,
            difference: honestData.win_rate - realtimeData.win_rate
          });
        }

        // Track directional differences (for side-aware mode)
        if (honestData.long_rate !== undefined && realtimeData.long_rate !== undefined) {
          const longRateDiff = Math.abs(honestData.long_rate - realtimeData.long_rate);
          if (longRateDiff > 15) {
            analysis.directional_differences.push({
              pattern,
              honest_long_rate: honestData.long_rate,
              realtime_long_rate: realtimeData.long_rate,
              difference: honestData.long_rate - realtimeData.long_rate
            });
          }
        }
      } else {
        analysis.honest_only.push(honestData);
      }
    }

    // Find realtime-only patterns
    for (const [pattern, realtimeData] of realtimeMap) {
      if (!honestMap.has(pattern)) {
        analysis.realtime_only.push(realtimeData);
      }
    }

    return analysis;
  }

  static validateConfig(config: Partial<WLLSHonestBacktestConfig>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.fileId) {
      errors.push('File ID is required');
    }

    if (typeof config.startPosition !== 'number' || config.startPosition < 10) {
      errors.push('Start position must be at least 10');
    }

    if (typeof config.patternRefreshFrequency !== 'number' || config.patternRefreshFrequency < 1) {
      errors.push('Pattern refresh frequency must be at least 1');
    }

    if (typeof config.winThreshold !== 'number' || config.winThreshold < 0.5 || config.winThreshold > 1) {
      errors.push('Win threshold must be between 0.5 and 1.0');
    }

    if (!config.backtestMode || !['binary', 'side_aware'].includes(config.backtestMode)) {
      errors.push('Backtest mode must be either "binary" or "side_aware"');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static exportToCSV(result: WLLSHonestBacktestResult): string {
    const headers = [
      'Position',
      'Prediction',
      'Actual Result',
      'Confidence',
      'Win Probability',
      'Long Probability',
      'Pattern Count',
      'Used Patterns',
      'New Patterns',
      'Skip Reason',
      'Is Correct',
      'Directional Correct'
    ];

    const rows = result.positions.map(pos => [
      pos.position,
      pos.prediction,
      pos.actualResult,
      pos.confidence.toFixed(4),
      pos.winProbability?.toFixed(4) || '',
      pos.longProbability?.toFixed(4) || '',
      pos.currentPatternCount,
      pos.usedPatterns,
      pos.newPatternsAdded,
      pos.skipReason || '',
      pos.prediction === 'SKIP' ? 'N/A' : 
        (pos.prediction === 'WIN' && pos.actualResult.startsWith('1')) || 
        (pos.prediction === 'LOSS' && pos.actualResult.startsWith('0')) ? 'CORRECT' : 'WRONG',
      // Directional accuracy (only for side-aware mode)
      result.config.backtestMode === 'side_aware' && pos.longProbability !== undefined && pos.shortProbability !== undefined ?
        (Math.abs(pos.longProbability - pos.shortProbability) > 5 ? // Only count clear directional bias
          ((pos.longProbability > pos.shortProbability && pos.actualResult.endsWith('L')) ||
           (pos.shortProbability > pos.longProbability && pos.actualResult.endsWith('S'))) ? 'CORRECT' : 'WRONG'
        : 'N/A') : 'N/A'
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }
}